import { Alert, Flex, Modal, Text } from '@mantine/core'
import { useDisclosure } from '@mantine/hooks'
import { IconInfoCircle } from '@tabler/icons-react'
import { useState } from 'react'
import { validate as isValidUUID } from 'uuid'

import { BasicBlock, Button, Table } from 'src/common/components'
import { BENEFITS } from 'src/default/config'
import { useGenerateFullEarningsReport } from 'src/default/hooks/washington-state'
import { IEligibility } from 'src/default/interfaces'

import styles from './single-account-reports.module.scss'

interface SingleAccountReportsProps {
  eligibility: IEligibility[]
}

const SingleAccountReports = (props: SingleAccountReportsProps) => {
  const generateFullEarningsReport = useGenerateFullEarningsReport()
  const [opened, { open, close }] = useDisclosure(false)
  const [errorMessage, setErrorMessage] = useState('')

  const paidLeaveEligibility = props.eligibility.find(
    (item) => item.benefit.id === BENEFITS.PAID_LEAVE,
  )
  const providerExternalId = paidLeaveEligibility?.providerExternalId
  const isUUID = providerExternalId && isValidUUID(providerExternalId)

  if (isUUID) {
    return null
  }

  const handleGenerateReport = async () => {
    if (!providerExternalId) return

    try {
      await generateFullEarningsReport.mutateAsync({
        esdUniqueId: providerExternalId,
      })
      open()
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        error?.message ||
        'Failed to generate report. Please try again later.'
      setErrorMessage(errorMessage)
      open()
    }
  }

  return (
    <>
      <BasicBlock className={styles.wrapper} paddings="small" title="Reports">
        <Table className={styles.table}>
          <Table.Row key={1} className={styles.row}>
            <Table.Column title="Name">
              Full Historical Earnings Report
            </Table.Column>
            <Table.Column title="Description">
              Generate Full historical earnings report for the selected account
            </Table.Column>
            <Table.Column title="Action">
              <Flex align="center" justify="center">
                <Button
                  className={styles.button}
                  icon="CloudUpload"
                  size="small"
                  onClick={handleGenerateReport}
                  disabled={!providerExternalId}
                  loading={generateFullEarningsReport.isPending}
                >
                  Generate Report
                </Button>
              </Flex>
            </Table.Column>
          </Table.Row>
          <></>
        </Table>
      </BasicBlock>
      <Modal opened={opened} onClose={close} title="Report Generation Status">
        <Alert
          variant="light"
          color={errorMessage ? 'red' : 'blue'}
          mb={10}
          icon={<IconInfoCircle />}
        >
          <Text
            size="sm"
            style={{ wordBreak: 'break-word', whiteSpace: 'pre-wrap' }}
          >
            {errorMessage ||
              `We're processing your request. You will get the report in your email.`}
          </Text>
        </Alert>
        <Button type="submit" size="small" onClick={close}>
          Ok
        </Button>
      </Modal>
    </>
  )
}

export default SingleAccountReports
